import React, { useState, useEffect } from "react";

const DataCollectionModal = () => {
  const [showModal, setShowModal] = useState(false);
  const [step, setStep] = useState(1); // Step 1: Email, Step 2: Survey, Step 3: Location
  const [email, setEmail] = useState("");
  const [preferences, setPreferences] = useState({
    mortgages: false,
    autoLoans: false,
    creditCards: false,
    personalLoans: false,
  });
  const [location, setLocation] = useState("");
  const [county, setCounty] = useState("");
  const [entryId, setEntryId] = useState(null); // Store the ID of the created Sheety entry

  const SHEETY_API_URL =
    "https://api.sheety.co/8665b8fb05801f2a465ec0b3b8dcdc28/bestRate/customer";

  // Fetch user location on mount
  useEffect(() => {
    const fetchLocation = async () => {
      try {
        const response = await fetch("https://ipapi.co/json/");
        const data = await response.json();
        const detectedLocation = `${data.city}, ${data.region}, ${data.country}`;
        setLocation(detectedLocation); // Set the IP-based location
      } catch (error) {
        console.error("Error fetching location:", error);
        setLocation("Unknown Location");
      }
    };

    fetchLocation();
  }, []);

  // Show modal only if the user has not completed it
  useEffect(() => {
    const hasCompletedModal = localStorage.getItem("hasCompletedModal");

    if (!hasCompletedModal) {
      const timer = setTimeout(() => setShowModal(true), 15000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Function to create a new entry in Sheety
  const createEntry = async () => {
    const data = {
      customer: {
        email: email,
        mortgage: preferences.mortgages ? "Yes" : "No",
        auto: preferences.autoLoans ? "Yes" : "No",
        credit: preferences.creditCards ? "Yes" : "No",
        personal: preferences.personalLoans ? "Yes" : "No",
        location: location, // IP-based location
        county: county || "Not Provided", // User-entered county
      },
    };

    try {
      const response = await fetch(SHEETY_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        const responseData = await response.json();
        setEntryId(responseData.customer.id); // Store the ID of the created entry
        console.log("Data successfully sent to Google Sheets:", responseData);
      } else {
        console.error("Failed to create entry:", response.statusText);
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  // Function to update an existing entry in Sheety
  const updateEntry = async () => {
    if (!entryId) return; // If no entry has been created, skip

    const data = {
      customer: {
        email: email,
        mortgage: preferences.mortgages ? "Yes" : "No",
        auto: preferences.autoLoans ? "Yes" : "No",
        credit: preferences.creditCards ? "Yes" : "No",
        personal: preferences.personalLoans ? "Yes" : "No",
        location: location, // IP-based location
        county: county || "Not Provided", // User-entered county
      },
    };

    try {
      const response = await fetch(`${SHEETY_API_URL}/${entryId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        console.log("Data successfully updated in Google Sheets:", data);
      } else {
        console.error("Failed to update entry:", response.statusText);
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (email.trim()) {
      setStep(2);
      if (!entryId) {
        await createEntry(); // Create the entry if it doesn't exist
      } else {
        await updateEntry(); // Update the entry if it already exists
      }
    } else {
      alert("Please enter a valid email address.");
    }
  };

  const handleSurveySubmit = async (e) => {
    e.preventDefault();
    setStep(3);
    await updateEntry(); // Update the entry with survey data
  };

  const handleFinalSubmit = async (e) => {
    e.preventDefault();
    await updateEntry(); // Update the entry with final data

    // Mark the modal as completed
    localStorage.setItem("hasCompletedModal", "true");
    setShowModal(false); // Close the modal
  };

  const updatePreference = (key, value) => {
    setPreferences((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  return (
    <>
      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <button
              className="modal-close-button"
              onClick={() => setShowModal(false)}
            >
              &times;
            </button>
            {step === 1 && (
              <div>
                <h2 className="modal-title">Rates change every day and are location dependent</h2>
                <p className="modal-text">
                  We'll send you updated rates at the end of every month, no
                  BS, just the best rates.
                </p>
                <form onSubmit={handleEmailSubmit} className="form">
  <input
    type="email"
    placeholder="Enter your email address"
    value={email}
    onChange={(e) => setEmail(e.target.value)}
    className="input"
    required
  />
  <p className="disclaimer">
    By entering your email, you agree to our{" "}
    <a href="/privacypolicy" target="_blank" rel="noopener noreferrer" className="privacy-link">
      Privacy Policy
    </a>.
  </p>
  <button type="submit" className="submit-button">
    Submit
  </button>
</form>


              </div>
            )}
            {step === 2 && (
              <div>
                <h2 className="modal-title">Do you want to see rates for anything else?</h2>
                <form onSubmit={handleSurveySubmit} className="modal-form">
                  <label className="modal-checkbox-label">
                    <input
                      type="checkbox"
                      checked={preferences.mortgages}
                      onChange={(e) =>
                        updatePreference("mortgages", e.target.checked)
                      }
                    />
                    Mortgages
                  </label>
                  <label className="modal-checkbox-label">
                    <input
                      type="checkbox"
                      checked={preferences.autoLoans}
                      onChange={(e) =>
                        updatePreference("autoLoans", e.target.checked)
                      }
                    />
                    Auto Loans
                  </label>
                  <label className="modal-checkbox-label">
                    <input
                      type="checkbox"
                      checked={preferences.creditCards}
                      onChange={(e) =>
                        updatePreference("creditCards", e.target.checked)
                      }
                    />
                    Credit Cards
                  </label>
                  <label className="modal-checkbox-label">
                    <input
                      type="checkbox"
                      checked={preferences.personalLoans}
                      onChange={(e) =>
                        updatePreference("personalLoans", e.target.checked)
                      }
                    />
                    Personal Loans
                  </label>
                  <button type="submit" className="modal-submit-button">
                    Next
                  </button>
                </form>
              </div>
            )}
            {step === 3 && (
              <div>
                <h2 className="modal-title">
                  Many Credit Union memberships are restricted by location
                </h2>
                <p className="modal-text">What county do you live in?</p>
                <form onSubmit={handleFinalSubmit} className="modal-form">
                  <input
                    type="text"
                    placeholder="Enter your county"
                    value={county}
                    onChange={(e) => setCounty(e.target.value)}
                    className="modal-input"
                  />
                  <button type="submit" className="modal-submit-button">
                    Submit
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default DataCollectionModal;
